import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'

import { useParams, useSearchParams, useNavigate } from 'react-router-dom'

import { Song } from '@/core/api/entities/song/song'
import permissionService, {
	PERMISSIONS,
} from '@/core/api/services/permissionService'
import {
	isAccessDeniedError,
	shouldRetryRequest,
} from '@/core/api/utils/httpErrors'
import { useAccountConfig } from '@/core/hooks/useAccountConfig'
import { usePlaylists, usePlaylistSample } from '@/core/hooks/usePlaylist'
import {
	useFavoriteSchedules,
	useScheduleDetails,
	useUpdateSchedule,
} from '@/core/hooks/useSchedule'
import useTranslation from '@/core/i18n/useTranslation'
import { Button } from '@/shared/components/atoms/Button'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/shared/components/molecules/Dialog/dialog'
import Player from '@/shared/components/organisms/Player/Player'
import { useNotificationContext } from '@/shared/context/NotificationProvider'

import { CalendarEvent } from './context/Data'
import { EventsProvider, useEvents } from './context/EventContext'
import ScheduleDetailsPageUI from './ScheduleDetailsPageUI'
import { convertEventsToSlots } from './utils/eventConverter'
import { getActivePlaylistForSchedule } from './utils/getActivePlaylistForSchedule'
import { convertTimeslotsToEvents } from './utils/timeslotConverter'

const ScheduleDetailsPage: React.FC = () => {
	const navigate = useNavigate()
	const { id } = useParams<{ id: string }>()
	const [searchParams] = useSearchParams()
	const [isEditModalOpen, setIsEditModalOpen] = useState(false)
	const [isAssignModalOpen, setIsAssignModalOpen] = useState(false)
	const [accessDeniedModalOpen, setAccessDeniedModalOpen] = useState(false)
	const [publicEditWarningOpen, setPublicEditWarningOpen] = useState(false)
	const editModeHandledRef = useRef(false)
	const redirectToRef = useRef<string>('/schedules')
	const { showNotification } = useNotificationContext()
	const { t, getTranslation, currentLanguage } = useTranslation()

	const isCreationMode =
		id === 'new' &&
		searchParams.has('mode') &&
		searchParams.get('mode') === 'create'
	const sourceScheduleId = searchParams.get('duplicateFrom')
	const shouldLoadDuplicateSource = isCreationMode && !!sourceScheduleId

	const isEditMode =
		id !== 'new' &&
		searchParams.has('mode') &&
		searchParams.get('mode') === 'edit'

	const {
		data: scheduleDetails,
		isLoading,
		error,
		refetch: refetchSchedule,
	} = useScheduleDetails({
		scheduleId: !isCreationMode ? id : undefined,
		config: useAccountConfig({
			params: {
				include: 'timeslots.timeslot_contents.playlists',
			},
		}),
		options: {
			enabled: !isCreationMode && !!id,
			retry: shouldRetryRequest,
		},
	})
	const { data: duplicateSourceScheduleDetails, error: duplicateSourceError } =
		useScheduleDetails({
			scheduleId: shouldLoadDuplicateSource ? sourceScheduleId : undefined,
			config: useAccountConfig({
				params: {
					include: 'timeslots.timeslot_contents.playlists',
				},
			}),
			options: {
				enabled: shouldLoadDuplicateSource,
				retry: shouldRetryRequest,
			},
		})

	const { data: favoriteSchedulesData } = useFavoriteSchedules({
		config: useAccountConfig({
			params: {
				per_page: 100,
			},
		}),
		options: {
			enabled: !isCreationMode,
		},
	})

	// Warn before redirecting on 403/404 errors for schedule query (more user-friendly)
	useEffect(() => {
		const err = error || duplicateSourceError
		if (!err) return
		if (!isAccessDeniedError(err)) return

		redirectToRef.current = '/schedules'
		setAccessDeniedModalOpen(true)
	}, [error, duplicateSourceError])

	// Fetch playlists to get their names for display
	const { data: playlistsResponse } = usePlaylists({
		config: useAccountConfig({
			params: {
				per_page: 100,
			},
		}),
	})

	const playlists = playlistsResponse?.data || []
	const playlistMap = new Map(playlists.map((p) => [p.id, p.title]))

	const { mutateAsync: updateSchedule } = useUpdateSchedule({
		config: useAccountConfig({}),
		options: {
			onSuccess: async () => {
				showNotification({
					message: 'Schedule updated successfully',
					type: 'success',
				})
				// IMPORTANT: Await refetch to get fresh data before navigating
				await refetchSchedule()
				navigate(`/schedules/${id}`)
			},
			onError: (error: any) => {
				showNotification({
					message:
						error?.response?.data?.message || 'Failed to update schedule',
					type: 'error',
				})
			},
		},
	})

	const shouldWarnBeforeEditingPublicSchedule = !!scheduleDetails?.public

	const requestOpenEditModal = useCallback(
		(source: 'header' | 'route') => {
			if (!id || isCreationMode) return

			if (shouldWarnBeforeEditingPublicSchedule) {
				setPublicEditWarningOpen(true)

				// If the user landed here via ?mode=edit and cancels, we should
				// not keep re-triggering the warning.
				if (source === 'route') {
					editModeHandledRef.current = true
				}
				return
			}

			setIsEditModalOpen(true)
			if (source === 'route') editModeHandledRef.current = true
		},
		[
			id,
			isCreationMode,
			shouldWarnBeforeEditingPublicSchedule,
			setIsEditModalOpen,
		]
	)

	useEffect(() => {
		// If user navigates to /schedules/:id?mode=edit, open edit (or warning)
		// once scheduleDetails is available.
		if (!isEditMode) return
		if (editModeHandledRef.current) return
		if (!scheduleDetails) return
		if (isEditModalOpen || publicEditWarningOpen) return

		requestOpenEditModal('route')
	}, [
		isEditMode,
		scheduleDetails,
		isEditModalOpen,
		publicEditWarningOpen,
		requestOpenEditModal,
	])

	const initialCalendarEvents = useMemo(() => {
		if (isCreationMode && duplicateSourceScheduleDetails) {
			return convertTimeslotsToEvents(
				duplicateSourceScheduleDetails,
				playlistMap
			)
		}

		if (!isCreationMode && scheduleDetails) {
			return convertTimeslotsToEvents(scheduleDetails, playlistMap)
		}

		return []
	}, [
		isCreationMode,
		duplicateSourceScheduleDetails,
		scheduleDetails,
		playlistMap,
	])

	const handleEditSave = async (
		_newTitle: string,
		_newDescription: string,
		titleTranslations: Record<string, string>,
		descriptionTranslations: Record<string, string>,
		isPublic?: boolean,
		contentCategoryIds?: number[],
		currentEvents?: CalendarEvent[],
		resellerId?: number | null
	) => {
		if (!scheduleDetails || !id) return

		// Use provided currentEvents if available (the latest state from the calendar)
		// otherwise fall back to scheduleDetails.timeslots (the server state)
		const timeslotsToTransform = currentEvents
			? convertEventsToSlots(currentEvents as any)
			: (scheduleDetails.timeslots || []).map((slot: any) => ({
					id: slot.id,
					name: slot.name,
					type: slot.type,
					priority: slot.priority,
					vevent: slot.vevent,
					timeslot_contents: (slot.timeslotContents || []).map(
						(content: any) => ({
							...(content.id ? { id: content.id } : {}),
							type: content.type,
							volume: content.metadata?.volume || 100,
							...(content.metadata?.minPercentage !== undefined
								? { min_percentage: content.metadata.minPercentage }
								: {}),
							...(content.metadata?.maxPercentage !== undefined
								? { max_percentage: content.metadata.maxPercentage }
								: {}),
							playlists: content.playlists || [],
						})
					),
				}))

		const canManageReseller = permissionService.currentUserHasAllPermissions([
			PERMISSIONS.CREATE_SCHEDULES,
			PERMISSIONS.UPDATE_SCHEDULES,
		])

		await updateSchedule({
			scheduleId: id,
			data: {
				title: titleTranslations,
				description: descriptionTranslations,
				public: isPublic ?? scheduleDetails.public,
				content_category_ids:
					contentCategoryIds ?? scheduleDetails.contentCategoryIds,
				...(canManageReseller && {
					reseller_id:
						resellerId !== undefined
							? resellerId
							: (scheduleDetails.resellerId ?? null),
				}),
				timeslots: timeslotsToTransform,
			},
		})
	}

	const title = isCreationMode
		? searchParams.get('name') ||
			getTranslation(
				duplicateSourceScheduleDetails?.titleTranlations || {},
				currentLanguage,
				duplicateSourceScheduleDetails?.title || ''
			)
		: getTranslation(
				scheduleDetails?.titleTranlations || {},
				currentLanguage,
				scheduleDetails?.title || searchParams.get('name') || ''
			)
	const description = isCreationMode
		? searchParams.get('description') ||
			getTranslation(
				duplicateSourceScheduleDetails?.descriptionTranlations || {},
				currentLanguage,
				duplicateSourceScheduleDetails?.description || ''
			)
		: getTranslation(
				scheduleDetails?.descriptionTranlations || {},
				currentLanguage,
				scheduleDetails?.description || searchParams.get('description') || ''
			)

	const favoriteScheduleIds = useMemo(() => {
		return new Set(
			(favoriteSchedulesData?.data || []).map((schedule) => schedule.id)
		)
	}, [favoriteSchedulesData])

	const isFavorite = useMemo(() => {
		if (
			scheduleDetails?.favorite !== undefined &&
			scheduleDetails?.favorite !== null
		) {
			return !!scheduleDetails.favorite
		}

		if (!id) return false
		return favoriteScheduleIds.has(Number(id))
	}, [scheduleDetails?.favorite, favoriteScheduleIds, id])

	const canEditSchedule = permissionService.currentUserHasAnyPermission([
		PERMISSIONS.MANAGE_CHILDREN_SCHEDULES,
		PERMISSIONS.MANAGE_OWN_LEVEL_SCHEDULES,
		PERMISSIONS.UPDATE_SCHEDULES,
	])
	const canUpdatePublicSchedule = permissionService.currentUserHasPermission(
		PERMISSIONS.UPDATE_SCHEDULES
	)
	// Public schedules require UPDATE_SCHEDULES; private schedules accept the
	// broader canEditSchedule set (manage_children | manage_own_level | update).
	const isReadOnly =
		!isCreationMode &&
		(scheduleDetails?.public ? !canUpdatePublicSchedule : !canEditSchedule)

	return (
		<>
			<Dialog
				open={accessDeniedModalOpen}
				onOpenChange={(open) => {
					if (!open) {
						setAccessDeniedModalOpen(false)
						navigate(redirectToRef.current, { replace: true })
						return
					}
					setAccessDeniedModalOpen(true)
				}}
			>
				<DialogContent className="sm:max-w-[520px]">
					<DialogHeader>
						<DialogTitle>{t('schedules.accessDenied.title')}</DialogTitle>
						<DialogDescription>
							{t('schedules.accessDenied.description')}
						</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button
							variant="outline"
							onClick={() => window.open('https://wa.me/15557052438', '_blank')}
						>
							{t('actions.contactSupport')}
						</Button>
						<Button
							onClick={() => {
								setAccessDeniedModalOpen(false)
								navigate(redirectToRef.current, { replace: true })
							}}
						>
							{t('actions.close')}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			<Dialog
				open={publicEditWarningOpen}
				onOpenChange={(open) => {
					if (!open) {
						setPublicEditWarningOpen(false)
						// If the warning was triggered by ?mode=edit, remove it so it
						// doesn't keep popping up.
						if (isEditMode && id) {
							navigate(`/schedules/${id}`, { replace: true })
						}
						return
					}
					setPublicEditWarningOpen(true)
				}}
			>
				<DialogContent className="sm:max-w-[520px]">
					<DialogHeader>
						<DialogTitle>{t('schedules.publicEditWarning.title')}</DialogTitle>
						<DialogDescription>
							{t('schedules.publicEditWarning.description')}
						</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button
							variant="outline"
							onClick={() => {
								setPublicEditWarningOpen(false)
								if (isEditMode && id) {
									navigate(`/schedules/${id}`, { replace: true })
								}
							}}
						>
							{t('actions.cancel')}
						</Button>
						<Button
							onClick={() => {
								setPublicEditWarningOpen(false)
								setIsEditModalOpen(true)
							}}
						>
							{t('actions.continue')}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			<EventsProvider
				key={`${id || 'new'}-${isCreationMode ? 'create' : 'details'}`}
				initialEvents={initialCalendarEvents}
			>
				<ScheduleDetailsPageContent
					id={id}
					isCreationMode={isCreationMode}
					onRequestEdit={() => requestOpenEditModal('header')}
					isLoading={isLoading}
					error={error}
					scheduleName={title}
					scheduleDescription={description}
					scheduleDetails={scheduleDetails}
					initialCalendarEvents={initialCalendarEvents}
					isFavorite={isFavorite}
					isReadOnly={isReadOnly || false}
					isEditModalOpen={isEditModalOpen}
					setIsEditModalOpen={setIsEditModalOpen}
					isAssignModalOpen={isAssignModalOpen}
					setIsAssignModalOpen={setIsAssignModalOpen}
					refetchSchedule={refetchSchedule}
					handleEditSave={handleEditSave}
					isPreviewDisabled={!scheduleDetails || isLoading || isCreationMode}
					playlists={playlists}
				/>
			</EventsProvider>
		</>
	)
}

export default ScheduleDetailsPage

const ScheduleDetailsPageContent: React.FC<{
	id?: string
	isCreationMode: boolean
	onRequestEdit: () => void
	isLoading: boolean
	error: any
	scheduleName: string
	scheduleDescription: string
	scheduleDetails: any
	initialCalendarEvents: CalendarEvent[]
	isFavorite: boolean
	isReadOnly: boolean
	isEditModalOpen: boolean
	setIsEditModalOpen: (open: boolean) => void
	isAssignModalOpen: boolean
	setIsAssignModalOpen: (open: boolean) => void
	refetchSchedule: () => void
	handleEditSave: (
		newTitle: string,
		newDescription: string,
		titleTranslations: Record<string, string>,
		descriptionTranslations: Record<string, string>,
		isPublic?: boolean,
		contentCategoryIds?: number[],
		currentEvents?: CalendarEvent[],
		resellerId?: number | null
	) => Promise<void>
	isPreviewDisabled: boolean
	playlists: any[]
}> = ({
	id,
	isCreationMode,
	isLoading,
	error,
	scheduleName,
	scheduleDescription,
	scheduleDetails,
	initialCalendarEvents,
	isFavorite,
	isReadOnly,
	isEditModalOpen,
	onRequestEdit,
	setIsEditModalOpen,
	isAssignModalOpen,
	setIsAssignModalOpen,
	refetchSchedule,
	handleEditSave,
	isPreviewDisabled,
	playlists,
}) => {
	const { t } = useTranslation()
	const { showNotification } = useNotificationContext()
	const accountConfig = useAccountConfig({})
	const { events } = useEvents()

	const playlistById = useMemo(
		() => new Map(playlists.map((playlist) => [playlist.id, playlist])),
		[playlists]
	)

	const previewTimeslots = useMemo(
		() => convertEventsToSlots(events as any),
		[events]
	)

	const previewSchedule = useMemo(() => {
		if (!scheduleDetails) return null

		return {
			...scheduleDetails,
			timeslots: previewTimeslots.map((slot) => ({
				id: slot.id ?? 0,
				name: slot.name,
				type: slot.type,
				priority: slot.priority,
				vevent: slot.vevent,
				timeslotContents: slot.timeslot_contents.map((content) => ({
					id: content.id ?? 0,
					timeslotId: slot.id ?? 0,
					type: content.type,
					metadata: { volume: content.volume },
					playlists: content.playlists.map((playlist) => {
						const playlistData = playlistById.get(playlist.id)
						if (playlistData) {
							return { ...playlistData, percentage: playlist.percentage }
						}
						return {
							id: playlist.id,
							title: '',
							percentage: playlist.percentage,
						}
					}),
				})),
			})),
		} as any
	}, [scheduleDetails, previewTimeslots, playlistById])

	const activePlaylist = previewSchedule
		? getActivePlaylistForSchedule(previewSchedule, new Date(), {
				fallbackToFirstPlaylist: false,
			})
		: null
	const activePlaylistId = activePlaylist?.id

	const { data: sampleSongs, isLoading: isSampleLoading } = usePlaylistSample({
		playlistId: activePlaylistId,
		config: accountConfig,
		options: {
			enabled: !!activePlaylistId && !isCreationMode,
		},
	})

	const [isPreviewPlaying, setIsPreviewPlaying] = useState(false)
	const [isPreviewLoading, setIsPreviewLoading] = useState(false)
	const [isPreviewStarting, setIsPreviewStarting] = useState(false)
	const [previewSongIndex, setPreviewSongIndex] = useState<number>(0)
	const isPreviewSkippingRef = useRef(false)
	const [previewSong, setPreviewSong] = useState<Song | null>(null)

	useEffect(() => {
		if (!isSampleLoading) {
			setIsPreviewLoading(false)
		}
	}, [isSampleLoading])

	const stopSchedulePreview = useCallback(() => {
		setPreviewSong(null)
		setPreviewSongIndex(0)
		setIsPreviewPlaying(false)
		setIsPreviewLoading(false)
		setIsPreviewStarting(false)
	}, [])

	const playSchedulePreview = useCallback(() => {
		if (!scheduleDetails || isCreationMode) return

		if (isPreviewPlaying) {
			stopSchedulePreview()
			return
		}

		if (isSampleLoading) {
			setIsPreviewLoading(true)
			return
		}

		try {
			const playlist = activePlaylist
			if (!playlist) {
				showNotification({
					type: 'error',
					message: t(
						'entities.schedule.card.preview_empty_now',
						'Nothing is assigned to the current time'
					),
				})
				setIsPreviewLoading(false)
				return
			}

			if (!sampleSongs || sampleSongs.length === 0) {
				showNotification({
					type: 'error',
					message: t(
						'entities.schedule.card.preview_no_sample',
						'No preview available for this playlist'
					),
				})
				setIsPreviewLoading(false)
				return
			}

			const firstSong = sampleSongs[0]

			setPreviewSongIndex(0)
			setPreviewSong(firstSong)
			setIsPreviewPlaying(true)
			setIsPreviewStarting(true)
			setIsPreviewLoading(false)
		} catch (error: any) {
			setIsPreviewLoading(false)
			setIsPreviewStarting(false)
			showNotification({
				type: 'error',
				message: t('common.try_again_later'),
				description: error?.message,
			})
		}
	}, [
		scheduleDetails,
		isCreationMode,
		isPreviewPlaying,
		isSampleLoading,
		activePlaylist,
		sampleSongs,
		stopSchedulePreview,
		showNotification,
		t,
	])

	useEffect(() => {
		return () => {
			stopSchedulePreview()
		}
	}, [stopSchedulePreview])

	const previewCover =
		scheduleDetails?.metadata?.mediumCovers?.[0] ||
		scheduleDetails?.metadata?.smallCovers?.[0] ||
		''

	const skipSchedulePreview = useCallback(() => {
		if (!sampleSongs || sampleSongs.length === 0) return
		if (sampleSongs.length === 1) return

		const nextIndex = (previewSongIndex + 1) % sampleSongs.length
		const nextSong = sampleSongs[nextIndex]

		isPreviewSkippingRef.current = true
		setPreviewSongIndex(nextIndex)
		setPreviewSong(nextSong)
	}, [sampleSongs, previewSongIndex])

	return (
		<>
			<ScheduleDetailsPageUI
				id={id}
				isCreationMode={isCreationMode}
				isLoading={isLoading}
				error={error}
				scheduleName={scheduleName}
				scheduleDescription={scheduleDescription}
				scheduleDetails={scheduleDetails}
				initialCalendarEvents={initialCalendarEvents}
				isFavorite={isFavorite}
				isReadOnly={isReadOnly}
				isEditModalOpen={isEditModalOpen}
				onRequestEdit={onRequestEdit}
				setIsEditModalOpen={setIsEditModalOpen}
				isAssignModalOpen={isAssignModalOpen}
				setIsAssignModalOpen={setIsAssignModalOpen}
				refetchSchedule={refetchSchedule}
				handleEditSave={handleEditSave}
				onPreviewPlay={playSchedulePreview}
				onPreviewSkip={skipSchedulePreview}
				isPreviewPlaying={isPreviewPlaying}
				isPreviewLoading={isPreviewLoading}
				isPreviewStarting={isPreviewStarting}
				isPreviewDisabled={isPreviewDisabled}
				isPreviewSkipDisabled={
					isPreviewDisabled ||
					isPreviewLoading ||
					isPreviewStarting ||
					!sampleSongs ||
					sampleSongs.length <= 1
				}
			/>

			{previewSong && activePlaylist && (
				<div className="hidden">
					<Player
						key={previewSong.path}
						src={previewSong.path}
						cover={previewCover}
						title={previewSong.metadata?.title || activePlaylist.title}
						radioPrevText={t(
							'entities.schedule.card.preview',
							'Schedule preview'
						)}
						radioTitle={activePlaylist.title}
						metadata={[previewSong.metadata?.artist || ''].filter(Boolean)}
						showSkipControls={false}
						showJumpControls={false}
						autoPlay={true}
						onPlay={() => {
							isPreviewSkippingRef.current = false
							setIsPreviewStarting(false)
						}}
						onPause={() => {
							if (isPreviewSkippingRef.current) {
								// Changing the song triggers an onPause event in the underlying
								// audio player. Ignore it so the preview keeps playing.
								isPreviewSkippingRef.current = false
								return
							}
							stopSchedulePreview()
						}}
						onEnded={stopSchedulePreview}
						hideVolume={true}
						cssVars={{
							border: 'none',
							backgroundColor: 'transparent',
						}}
					/>
				</div>
			)}
		</>
	)
}
